import React, { createContext, use, useMemo } from "react";
import { addQToItem } from "@/utils/helpers";
import { useBasicDataQuery } from "@/services/basicData/basicData";
import { useGetHouseContractsQuery } from "@/services/makkahGroups";
import { useGetHouseExaminationsQuery } from "@/services/housing";
import { useContracts } from "@/utils/hooks/useContracts";
import { useLocation } from "react-router";
import { useAuthContext } from "@/_helper/Auth";

export const CachedDataContext = createContext(null);

export const CachedDataProvider = ({ children }) => {
  const { token } = useAuthContext()

  const { data: basicDataResponse, ...restBasicDataQuery } = useBasicDataQuery({
    enabled: !!token,
  });
  const { contracts, makkahContracts, madinahContracts, ...restContractsDataQuery } =
    useContracts({ enabled: !!token });
  const { data: houseExaminationsRes, ...restHouseExaminationsQuery } =
    useGetHouseExaminationsQuery({ enabled: !!token });


  const houseExaminations = houseExaminationsRes?.data?.data ?? {};

  const basicData = useMemo(
    () => basicDataResponse?.data?.data ?? {},
    [basicDataResponse]
  );

  // Build Maps and arrays for fast lookup + iteration
  const cachedData = useMemo(() => {
    const maps = {};
    const arrays = {};

    // Process basic data (cities, ports, etc.)
    Object.entries(basicData).forEach(([key, arr]) => {
      if (Array.isArray(arr)) {
        const itemsWithQ = arr.map(addQToItem);
        maps[key] = new Map(itemsWithQ.map(item => [String(item.id), item]));
        arrays[key] = itemsWithQ;
      }
    });

    // Process house examinations
    if (!!houseExaminations) {
      const { enums: enumData, ...houseExaminationsRest } = houseExaminations;

      Object?.entries({ ...houseExaminations, ...enumData } ?? {}).forEach(([key, arr]) => {
        if (Array.isArray(arr)) {
          const itemsWithQ = arr.map(addQToItem);
          maps[key] = new Map(itemsWithQ.map(item => [String(item.id), item]));
          arrays[key] = itemsWithQ;
        }
      });
    }

    // Process contracts (all three types)
    if (Array.isArray(contracts)) {
      const contractsWithQ = contracts.map(addQToItem);
      maps.contracts = new Map(contractsWithQ.map(item => [String(item.id), item]));
      arrays.contracts = contractsWithQ;
    }

    if (Array.isArray(makkahContracts)) {
      const makkahContractsWithQ = makkahContracts.map(addQToItem);
      maps.makkahContracts = new Map(
        makkahContractsWithQ.map(item => [String(item.id), item])
      );
      arrays.makkahContracts = makkahContractsWithQ;
    }

    if (Array.isArray(madinahContracts)) {
      const madinahContractsWithQ = madinahContracts.map(addQToItem);
      maps.madinahContracts = new Map(
        madinahContractsWithQ.map(item => [String(item.id), item])
      );
      arrays.madinahContracts = madinahContractsWithQ;
    }

    return { maps, arrays };
  }, [basicData, houseExaminations, contracts, makkahContracts, madinahContracts]);


  const value = {
    // Maps for O(1) instant lookup by id
    cachedMaps: cachedData.maps,
    // Arrays for iteration/filtering/backwards compatibility
    cachedArrays: cachedData.arrays,
    // Legacy compatibility object while old consumers are being removed
    cachedBasicData: cachedData.arrays,
    // Legacy compatibility response buckets
    basicDataResponse: { ...basicDataResponse, ...restBasicDataQuery },
    contractsDataResponse: restContractsDataQuery,
    houseExaminationsDataResponse: restHouseExaminationsQuery,
    // Utility: find item by category and id (O(1))
    findById: (category, id) => cachedData.maps[category]?.get(String(id)),
    // Utility: find item by any field with optional filter (optimized O(n) single pass)
    findByField: (category, fieldName, value, filter) => {
      const arr = cachedData.arrays[category];
      if (!Array.isArray(arr)) return undefined;

      // If field is "id", use Map for O(1) - but still apply filter if needed
      if (fieldName === "id") {
        const item = cachedData.maps[category]?.get(String(value));
        if (!item) return undefined;
        return filter ? (filter(item) ? item : undefined) : item;
      }

      // For other fields, do single-pass search with filter
      return arr.find((item) => {
        const fieldMatch = String(item[fieldName]) === String(value);
        if (!fieldMatch) return false;
        return filter ? filter(item) : true;
      });
    },
    // Utility: get full array for a category
    getArray: (category) => cachedData.arrays[category] ?? [],
    // Loading states
    isLoading:
      restBasicDataQuery.isLoading ||
      restContractsDataQuery.isLoading ||
      restHouseExaminationsQuery.isLoading,
    isFetching:
      restBasicDataQuery.isFetching ||
      restContractsDataQuery.isFetching ||
      restHouseExaminationsQuery.isFetching,
  };

  return (
    <CachedDataContext.Provider value={value}>
      {children}
    </CachedDataContext.Provider>
  );
};
